import { useEffect, useState } from 'react'

interface UseClipFailureTimeoutIProps {
  /** Incident creation time (ISO). The deadline is anchored to this, not to mount. */
  createdAt?: string
  timeoutMs: number
  /**
   * True once the clip arrived (or was never expected). Keeps the deadline from ever being
   * reported as elapsed, and lets a late-arriving clip clear an already-shown error state.
   */
  isResolved: boolean
}

/**
 * Whether a clip kind's failure deadline has passed with the clip still unresolved.
 *
 * Anchored to the incident's age so an old incident with a missing clip reports failure on the
 * first render instead of waiting out the window again. Arms a single setTimeout rather than an
 * interval — it fires exactly at the deadline, so there are no re-renders while waiting.
 *
 * A missing or unparseable `createdAt` counts as NOT elapsed: we're waiting on incident data, and
 * claiming failure there would flash the error state before the drawer has loaded.
 */
export const useClipFailureTimeout = ({
  createdAt,
  timeoutMs,
  isResolved,
}: UseClipFailureTimeoutIProps): boolean => {
  const createdAtMs = Date.parse(createdAt ?? '')
  const isValidDate = !Number.isNaN(createdAtMs)

  const computeHasElapsed = () =>
    isValidDate && Date.now() - createdAtMs >= timeoutMs

  const [hasElapsed, setHasElapsed] = useState(computeHasElapsed)

  // Re-sync when the anchor or the threshold changes (e.g. incident data arrives late).
  useEffect(() => {
    setHasElapsed(computeHasElapsed())
  }, [createdAtMs, timeoutMs])

  useEffect(() => {
    if (!isValidDate || hasElapsed) return

    const remainingMs = Math.max(0, createdAtMs + timeoutMs - Date.now())
    if (remainingMs === 0) {
      setHasElapsed(true)
      return
    }

    const timeoutId = setTimeout(() => setHasElapsed(true), remainingMs)
    return () => clearTimeout(timeoutId)
  }, [createdAtMs, timeoutMs, hasElapsed, isValidDate])

  return hasElapsed && !isResolved
}
